/**
 * InteractiveBlocks — Maneja interacciones especiales con bloques.
 *
 * - Trapdoor: Se abre/cierra con click derecho (toggle visual, cambia colision)
 * - Sign: Click derecho para escribir texto, muestra texto flotante
 * - Jukebox: Click derecho para reproducir/detener musica procedural
 * - Noteblock: Click derecho cambia tono, click izquierdo reproduce nota
 * - TNT: Explosion mejorada con drops de bloques y reaccion en cadena
 */
import { BlockId } from './BlockType.js';

export class InteractiveBlocks {
  constructor(world, soundManager) {
    this.world = world;
    this.soundManager = soundManager;
    this._signs = new Map();     // "x,y,z" -> texto string
    this._trapdoors = new Map(); // "x,y,z" -> { open: boolean }
    this._noteblocks = new Map(); // "x,y,z" -> { pitch: 0-24 }
    this._jukeboxPlaying = new Map(); // "x,y,z" -> timeout id or null
    this._coffins = new Map(); // "x,y,z" -> { open: boolean }
  }

  // Click derecho en trapdoor
  toggleTrapdoor(x, y, z) {
    const key = `${x},${y},${z}`;
    const state = this._trapdoors.get(key) || { open: false };
    state.open = !state.open;
    this._trapdoors.set(key, state);
    return state.open;
  }

  isTrapdoorOpen(x, y, z) {
    const key = `${x},${y},${z}`;
    const state = this._trapdoors.get(key);
    return state ? state.open : false;
  }

  // Click derecho en cartel — retorna texto actual (el llamador muestra dialogo)
  getSignText(x, y, z) {
    return this._signs.get(`${x},${y},${z}`) || '';
  }

  setSignText(x, y, z, text) {
    this._signs.set(`${x},${y},${z}`, text.slice(0, 50)); // max 50 caracteres
  }

  // Click derecho en noteblock — cambiar tono
  changeNotePitch(x, y, z) {
    const key = `${x},${y},${z}`;
    const state = this._noteblocks.get(key) || { pitch: 0 };
    state.pitch = (state.pitch + 1) % 25; // 0-24 = 2 octavas
    this._noteblocks.set(key, state);
    this._playNote(state.pitch);
    return state.pitch;
  }

  // Click izquierdo en noteblock — reproducir tono actual
  playNoteBlock(x, y, z) {
    const key = `${x},${y},${z}`;
    const state = this._noteblocks.get(key) || { pitch: 0 };
    this._playNote(state.pitch);
  }

  _playNote(pitch) {
    // Usar Web Audio API para reproducir una nota
    // Frecuencia = 440 * 2^((pitch - 12) / 12) para notas musicales
    if (!this.soundManager || !this.soundManager.ctx) return;
    const ctx = this.soundManager.ctx;
    if (ctx.state === 'suspended') ctx.resume();
    const freq = 440 * Math.pow(2, (pitch - 12) / 12);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  }

  // Explosion de TNT con mecanicas mejoradas
  explodeTNT(x, y, z, radius = 4) {
    const destroyed = [];
    const chainTNT = [];
    // Esfera de destruccion
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dz = -radius; dz <= radius; dz++) {
          if (dx * dx + dy * dy + dz * dz > radius * radius) continue;
          const bx = x + dx, by = y + dy, bz = z + dz;
          const block = this.world.getBlock(bx, by, bz);
          if (block === BlockId.AIR || block === BlockId.BEDROCK) continue;

          // Probabilidad de no destruir (anillo exterior)
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (dist > radius * 0.7 && Math.random() < 0.4) continue;

          // Reaccion en cadena: si el bloque destruido es TNT
          if (block === BlockId.TNT && (dx !== 0 || dy !== 0 || dz !== 0)) {
            chainTNT.push({ x: bx, y: by, z: bz });
          }

          destroyed.push({ x: bx, y: by, z: bz, blockId: block });
          this.world.setBlock(bx, by, bz, BlockId.AIR);
        }
      }
    }

    // Reaccion en cadena con delay
    chainTNT.forEach((pos, i) => {
      setTimeout(() => {
        this.explodeTNT(pos.x, pos.y, pos.z, radius);
      }, 300 + i * 200);
    });

    return destroyed; // Array de bloques destruidos para drops
  }

  // Toggle jukebox (reproducir/detener musica)
  toggleJukebox(x, y, z) {
    const key = `${x},${y},${z}`;
    if (this._jukeboxPlaying.has(key)) {
      // Detener
      const timeoutId = this._jukeboxPlaying.get(key);
      if (timeoutId) clearTimeout(timeoutId);
      this._jukeboxPlaying.delete(key);
      return false;
    } else {
      // Reproducir una melodia procedural
      this._playJukeboxMelody(key);
      return true;
    }
  }

  _playJukeboxMelody(key) {
    if (!this.soundManager || !this.soundManager.ctx) return;
    const ctx = this.soundManager.ctx;
    if (ctx.state === 'suspended') ctx.resume();
    // Melodia simple: escala de Do mayor repetida
    const notes = [261.63, 293.66, 329.63, 349.23, 392.00, 440.00, 493.88, 523.25];
    const now = ctx.currentTime;
    for (let i = 0; i < 16; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = notes[i % notes.length];
      gain.gain.setValueAtTime(0.15, now + i * 0.3);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.3 + 0.25);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.3);
      osc.stop(now + i * 0.3 + 0.3);
    }
    // Marcar como reproduciendo por ~5 segundos
    const timeoutId = setTimeout(() => this._jukeboxPlaying.delete(key), 5000);
    this._jukeboxPlaying.set(key, timeoutId);
  }

  // Toggle coffin open/close
  toggleCoffin(x, y, z) {
    const key = `${x},${y},${z}`;
    const block = this.world.getBlock(x, y, z);
    if (block === BlockId.COFFIN) {
      this.world.setBlock(x, y, z, BlockId.COFFIN_OPEN);
      this._coffins.set(key, { open: true });
      return true; // now open
    } else if (block === BlockId.COFFIN_OPEN) {
      this.world.setBlock(x, y, z, BlockId.COFFIN);
      this._coffins.set(key, { open: false });
      return false; // now closed
    }
    return null;
  }

  isCoffinOpen(x, y, z) {
    const block = this.world.getBlock(x, y, z);
    return block === BlockId.COFFIN_OPEN;
  }

  // Limpiar cuando se elimina un bloque
  onBlockRemoved(x, y, z) {
    const key = `${x},${y},${z}`;
    this._signs.delete(key);
    this._trapdoors.delete(key);
    this._noteblocks.delete(key);
    if (this._jukeboxPlaying.has(key)) {
      const timeoutId = this._jukeboxPlaying.get(key);
      if (timeoutId) clearTimeout(timeoutId);
      this._jukeboxPlaying.delete(key);
    }
    this._coffins.delete(key);
  }

  serialize() {
    return {
      signs: Object.fromEntries(this._signs),
      trapdoors: Object.fromEntries(this._trapdoors),
      noteblocks: Object.fromEntries(this._noteblocks),
      coffins: Object.fromEntries(this._coffins),
    };
  }

  deserialize(data) {
    if (!data) return;
    if (data.signs) this._signs = new Map(Object.entries(data.signs));
    if (data.trapdoors) this._trapdoors = new Map(Object.entries(data.trapdoors));
    if (data.noteblocks) this._noteblocks = new Map(Object.entries(data.noteblocks));
    if (data.coffins) this._coffins = new Map(Object.entries(data.coffins));
  }
}
